import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
// import { Switch } from '@/components/ui/switch';
import { Loader2, Save } from 'lucide-react';
import { supabase } from '@/services';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/providers/auth-provider';
import { useTranslation } from 'react-i18next';
import Settings from '@/components/settings';
import packageJson from '@/../package.json';

// Initialize Supabase client

export default function Profile() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { toast } = useToast();
  const updateProfile = useMutation({
    mutationFn: async (userData: {
      first_name: string;
      last_name: string;
      phone: string;
    }) => {
      const userId = user?.id;
      if (!userId) throw new Error('User not found');
      const { data, error } = await supabase.auth.updateUser({
        data: {
          first_name: userData?.first_name,
          last_name: userData?.last_name,
          phone: userData?.phone,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Profile updated',
        description: t('profileUpdated'),
        duration: 5000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'destructive',
        duration: 5000,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    updateProfile.mutate({
      first_name: formData.get('first_name') as string,
      last_name: formData.get('last_name') as string,
      phone: formData.get('phone') as string,
    });
  };

  return (
    <div className="container mx-auto py-10">
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">{t('profile')}</CardTitle>
          <CardDescription>{t('communicationPrefrences')}</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">{t('firstName')}</Label>
                  <Input
                    autoFocus
                    id="first_name"
                    name="first_name"
                    autoComplete="given-name"
                    defaultValue={user?.user_metadata?.first_name || ''}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">{t('lastName')}</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    autoComplete="family-name"
                    defaultValue={user?.user_metadata?.last_name || ''}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <Label htmlFor="phone">{t('phone')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="phone"
                  autoComplete="mobile tel"
                  defaultValue={user?.phone || ''}
                />
              </div>
              {/* <div className="space-y-4 mt-6">
                <div className="flex items-center justify-between">
                  <Label htmlFor="sms_consent" className="flex items-center space-x-2">
                    <span>{t('receiveSMS')}</span>
                  </Label>
                  <Switch
                    id="sms_consent"
                    name="sms_consent"
                    defaultChecked={user?.user_metadata?.sms_consent || false}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="email_consent" className="flex items-center space-x-2">
                    <span>{t('receiveEmail')}</span>
                  </Label>
                  <Switch
                    id="email_consent"
                    name="email_consent"
                    defaultChecked={user?.user_metadata?.email_consent || false}
                  />
                </div>
              </div> */}
            </motion.div>
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={updateProfile.isPending}>
              {updateProfile.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('updating')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('save')}
                </>
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
      <div className="mt-4">
        <Settings />
      </div>

      <div className="text-center mt-4">
        <p className="text-sm text-gray-500">Version: {packageJson.version}</p>
      </div>
    </div>
  );
}
